import React, { createContext, useContext, useReducer, useEffect, ReactNode, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { login as loginAPI, logout as logoutAPI, User, LoginPayload, getMe } from '../services/authService';
import { getAccessToken, clearTokens } from '../utils/tokenUtils';
import api from '../services/apiClient';

const apiConfig = {
  headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache', 'Expires': '0' },
};

interface AuthState {
  user: User | null;
  isLoggedIn: boolean;
  loading: boolean;
}

type AuthAction =
  | { type: 'LOGIN'; payload: { user: User } }
  | { type: 'LOGOUT' }
  | { type: 'INIT_START' }
  | { type: 'INIT_FINISH'; payload: { user: User | null } }
  | { type: 'UPDATE_USER'; payload: { user: User } };

const initialState: AuthState = {
  user: null,
  isLoggedIn: false,
  loading: true,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN': return { ...state, user: action.payload.user, isLoggedIn: true, loading: false };
    case 'LOGOUT': return { ...state, user: null, isLoggedIn: false, loading: false };
    case 'INIT_START': return { ...state, loading: true };
    case 'INIT_FINISH': return { ...state, user: action.payload.user, isLoggedIn: !!action.payload.user, loading: false };
    case 'UPDATE_USER':
      if (JSON.stringify(state.user) === JSON.stringify(action.payload.user)) {
        return state;
      }
      return { ...state, user: action.payload.user };
    default: return state;
  }
};

interface AuthContextType extends AuthState {
  login: (payload: LoginPayload) => Promise<void>;
  logout: () => void;
  refetchUser: () => Promise<void>;
  decrementNotificationCount: () => void;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const refetchUser = useCallback(async () => {
    try {
      const { data: freshUser } = await api.get<User>('/users/me', apiConfig);
      dispatch({ type: 'UPDATE_USER', payload: { user: freshUser } });
    } catch (error) {
      console.error("Manual user refetch failed:", error);
    }
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      dispatch({ type: 'INIT_START' });
      if (getAccessToken()) {
        try {
          const { data: user } = await api.get<User>('/users/me', apiConfig);
          dispatch({ type: 'INIT_FINISH', payload: { user } });
        } catch {
          clearTokens();
          dispatch({ type: 'INIT_FINISH', payload: { user: null } });
        }
      } else {
        dispatch({ type: 'INIT_FINISH', payload: { user: null } });
      }
    };
    initAuth();
  }, []);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    if (state.isLoggedIn && state.user) {
      intervalId = setInterval(refetchUser, 60000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [state.isLoggedIn, state.user, refetchUser]);

  const login = async (payload: LoginPayload) => {
    const response = await loginAPI(payload);
    dispatch({ type: 'LOGIN', payload: { user: response.user } });
  };

  const logout = () => {
    logoutAPI();
    dispatch({ type: 'LOGOUT' });
  };

  const decrementNotificationCount = () => {};

  return (
    <AuthContext.Provider value={{ ...state, login, logout, refetchUser, decrementNotificationCount }}>
      {state.loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};
